<template>
  <div class="online-page">
    <!-- Titel der Seite -->
    <h1>Online Benutzer</h1>

    <div v-if="loading" class="state-box">Lade Benutzer...</div>

    <div v-else-if="!users.length" class="state-box empty">
      Es ist kein angemeldeter Benutzer online.
    </div>

    <div v-else class="users-grid">
      <!-- Iteriert über die Liste der Online-Benutzer und zeigt für jeden eine Karte an -->
      <div v-for="u in users" :key="u.email" class="user-card">
        <img class="avatar" :src="resolveApiUrl(u.profileimage || DEFAULT_PROFILE_IMAGE)" @error="onImageError" />

        <div class="info-wrapper">
          <div class="info">
            <div class="name">
              {{ u.nickname || u.email.split("@")[0] }}
            </div>

            <div class="email">{{ u.email }}</div>
          </div>

          <div class="actions" :class="{ single: isOwnUser(u) }">
            <!-- Wenn es der eigene Benutzer ist, zeige einen Link zum Profil -->
            <router-link v-if="isOwnUser(u)" class="btn secondary" to="/me">Profil</router-link>
            <template v-else>
              <!-- Andernfalls zeige einen Button, um einen Direktchat zu starten -->
              <router-link class="btn primary" :to="`/dm/${encodeURIComponent(u.email)}`">Chat</router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue"
import { getOnlineUsers, resolveApiUrl } from "@/services/api"
import { DEFAULT_PROFILE_IMAGE } from "@/assets/config"
import { onImageError } from "@/utils/image"
import { useAuth } from "@/composables/useAuth"

// Lokale Zustandsvariablen für die Benutzerliste und den Ladezustand.
const users = ref([])
const loading = ref(true)

// Holt den aktuellen Benutzerstatus aus dem globalen Auth-Composable.
const { user } = useAuth()

/**
 * Normalisiert einen String für Vergleiche (lowercase, no whitespace).
 * @param {string} value - Der zu normalisierende String.
 */
const normalize = (value) => String(value ?? "").trim().toLowerCase()

/**
 * Prüft, ob der übergebene Benutzer der aktuell eingeloggte Benutzer ist.
 * @param {object} u - Das Benutzerobjekt aus der Online-Liste.
 * @returns {boolean}
 */
const isOwnUser = (u) => {
  const me = user.value
  if (!me || !u) return false
  // Vergleicht zuerst über die ID (falls vorhanden), ansonsten über die normalisierte E-Mail.
  if (me.id != null && u.id != null) return String(me.id) === String(u.id)
  return normalize(me.email) === normalize(u.email)
}

// Lifecycle-Hook: Wird ausgeführt, wenn die Komponente eingehängt wird.
onMounted(async () => {
  try {
    // Ruft die Liste der Online-Benutzer vom Backend ab.
    const res = await getOnlineUsers()
    users.value = res?.answer || []
  } finally {
    loading.value = false
  }
})
</script>

<style scoped lang="scss">
.online-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 5rem 2rem 2rem;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.user-card {
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 1rem;
  background: $card-background;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: $border-color;
  }
}

.avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid $border-color;
  flex-shrink: 0;
}

.info-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
}

.info {
  display: flex;
  flex-direction: column;
}

.name {
  font-weight: 700;
}

.email {
  font-size: 0.85rem;
  opacity: 0.7;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.actions.single .btn {
  flex: 1;
}

.btn {
  flex: 1;
  text-align: center;
  padding: 0.55rem;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: 0.15s ease;
}

.btn.primary {
  background: $primary-color;
  color: white;

  &:hover {
    background: $primary-color-hover;
  }
}

.btn.secondary {
  border: 1px solid $border-color;
  color: $text-color;

  &:hover {
    border-color: $primary-color;
  }
}

.state-box.empty {
  opacity: 0.8;
  text-align: center;
}
</style>